import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { CreditCard } from '@/types';
import { useAuth } from '../AuthContext';
import { useProfile } from '../ProfileContext';
import { useEncryption } from '../EncryptionContext';
import { useSubscription } from '../SubscriptionContext';
import { loadCardsFromSupabase } from '@/lib/supabase-operations';
import { cardsReducer, initialState } from './reducer';
import { useCardSelectors } from './selectors';
import { createCard, updateCardData, deleteCard, updateCardUsage as updateCardUsageOp } from './operations';
import { logger } from '@/utils/logger';

interface CardsContextType {
  cards: CreditCard[];
  addCard: (card: Omit<CreditCard, 'id' | 'used' | 'available' | 'createdAt'>) => Promise<CreditCard>;
  updateCard: (id: string, updates: Partial<CreditCard>) => Promise<void>;
  deleteCard: (id: string) => Promise<void>;
  getCardById: (id: string) => CreditCard | undefined;
  updateCardUsage: (cardId: string, amount: number) => void;
  releaseCardLimit: (cardId: string, amount: number) => void;
  getAvailableLimit: (cardId: string) => number;
  isLoading: boolean;
  error: string | null;
}

const CardsContext = createContext<CardsContextType | undefined>(undefined);

export const CardsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cardsReducer, initialState);
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { planLimits } = useSubscription();
  const { isEncryptionReady, isCheckingEncryption } = useEncryption();
  
  const selectors = useCardSelectors(state.cards);

  // Load cards from Supabase
  useEffect(() => {
    if (!user) {
      dispatch({ type: 'CLEAR_CARDS' });
      return;
    }

    if (isCheckingEncryption) {
      logger.debug('[Cards] Waiting for encryption check...');
      return;
    }
    
    if (!isEncryptionReady) {
      logger.warn('[Cards] Encryption not ready, clearing cards');
      dispatch({ type: 'CLEAR_CARDS' });
      return;
    }

    const loadCards = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      try {
        logger.info('[Cards] Loading cards from Supabase');
        const supabaseCards = await loadCardsFromSupabase();
        
        // Filter out invalid/incomplete cards
        const validCards = supabaseCards.filter(card => 
          card.name && card.name.trim() !== '' && 
          card.limit > 0
        );
        
        // Log invalid cards for debugging
        const invalidCards = supabaseCards.filter(card => 
          !card.name || card.name.trim() === '' || card.limit <= 0
        );
        if (invalidCards.length > 0) {
          logger.warn('[Cards] Cartões com dados incompletos detectados', { 
            count: invalidCards.length,
            ids: invalidCards.map(c => c.id)
          });
        }
        
        dispatch({ type: 'SET_CARDS', payload: validCards });
        logger.info('[Cards] Loaded valid cards', { count: validCards.length });
      } catch (error) {
        logger.error('[Cards] Error loading cards', { error });
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao carregar cartões' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadCards();
  }, [user, isEncryptionReady, isCheckingEncryption]);

  // Reload when profile changes
  useEffect(() => {
    if (user && activeProfile && isEncryptionReady && !isCheckingEncryption) {
      logger.info('[Cards] Profile changed, reloading cards', { profileId: activeProfile.id });
      
      const loadCards = async () => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
          const supabaseCards = await loadCardsFromSupabase();
          
          // Filter out invalid/incomplete cards
          const validCards = supabaseCards.filter(card => 
            card.name && card.name.trim() !== '' && 
            card.limit > 0
          );
          
          dispatch({ type: 'SET_CARDS', payload: validCards });
        } catch (error) {
          logger.error('[Cards] Error reloading cards', { error });
          dispatch({ type: 'SET_ERROR', payload: 'Erro ao carregar cartões' });
        }
      };
      
      loadCards();
    }
  }, [activeProfile?.id, user, isEncryptionReady, isCheckingEncryption]);

  // Listen for account deletion events to remove orphaned cards from local state
  useEffect(() => {
    const handleAccountDeleted = (event: Event) => {
      const customEvent = event as CustomEvent<{ accountId: string }>;
      const { accountId } = customEvent.detail;
      
      logger.info('[Cards] Account deleted event received, filtering cards', { accountId });
      
      dispatch({ 
        type: 'SET_CARDS', 
        payload: state.cards.filter(c => c.accountId !== accountId) 
      });
    };

    window.addEventListener('account-deleted', handleAccountDeleted);
    return () => window.removeEventListener('account-deleted', handleAccountDeleted);
  }, [state.cards]);

  const addCard = useCallback(async (
    cardData: Omit<CreditCard, 'id' | 'used' | 'available' | 'createdAt'>
  ): Promise<CreditCard> => {
    try {
      const newCard = await createCard(cardData, planLimits, state.cards.length);
      dispatch({ type: 'ADD_CARD', payload: newCard });
      dispatch({ type: 'SET_ERROR', payload: null });
      return newCard;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao criar cartão';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, [planLimits, state.cards.length]);

  const updateCard = useCallback(async (id: string, updates: Partial<CreditCard>) => {
    try {
      const currentCard = state.cards.find(c => c.id === id);
      if (!currentCard) throw new Error('Card not found');

      const updatedCard = await updateCardData(id, updates, currentCard);
      dispatch({ type: 'UPDATE_CARD', payload: { id, updates: updatedCard } });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao atualizar cartão';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, [state.cards]);

  const deleteCardFn = useCallback(async (id: string) => {
    try {
      await deleteCard(id);
      dispatch({ type: 'DELETE_CARD', payload: id });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao excluir cartão';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, []);

  const updateCardUsageFn = useCallback((cardId: string, amount: number) => {
    const card = state.cards.find(c => c.id === cardId);
    if (!card) return;

    dispatch({ type: 'UPDATE_CARD_USAGE', payload: { cardId, amount } });
    
    // Save async
    updateCardUsageOp(card, amount).catch(error => {
      logger.error('Error updating card usage in Supabase', { error });
    });
  }, [state.cards]);

  const releaseCardLimit = useCallback((cardId: string, amount: number) => {
    updateCardUsageFn(cardId, -amount);
  }, [updateCardUsageFn]);

  const value = useMemo<CardsContextType>(() => ({
    cards: state.cards,
    addCard,
    updateCard,
    deleteCard: deleteCardFn,
    getCardById: selectors.getById,
    updateCardUsage: updateCardUsageFn,
    releaseCardLimit,
    getAvailableLimit: selectors.getAvailableLimit,
    isLoading: state.isLoading,
    error: state.error,
  }), [state, addCard, updateCard, deleteCardFn, selectors, updateCardUsageFn, releaseCardLimit]);

  return (
    <CardsContext.Provider value={value}>
      {children}
    </CardsContext.Provider>
  );
};

export const useCards = () => {
  const context = useContext(CardsContext);
  if (!context) {
    throw new Error('useCards must be used within a CardsProvider');
  }
  return context;
};
